// pages/tabs/MeterResetTab.jsx
import React, { useEffect, useState } from "react";
import { Card, Form, Button, Spinner, Badge, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  fetchLastReadingThunk,
  resetMeterThunk,
  clearMeterResetError,
} from "../../../../store/slices/meterResetSlice";

import "../../../../css/master-common.css";

export default function MeterResetTab({ home_id, activeTab }) {
  const dispatch = useDispatch();
  const { lastReading, loading, saving, error } = useSelector((s) => s.meterReset);

  const [newReading, setNewReading] = useState("");

  // ✅ must match tab key from HouseProfilePage
  const isActive = activeTab === "reset";

  // ✅ fetch ONLY when tab becomes active
  useEffect(() => {
    if (!isActive) return;
    if (home_id) dispatch(fetchLastReadingThunk(home_id));
  }, [isActive, home_id, dispatch]);

  // toast errors
  useEffect(() => {
    if (!error) return;
    toast.error(String(error));
    dispatch(clearMeterResetError());
  }, [error, dispatch]);

  const submit = async (e) => {
    e.preventDefault();

    const val = String(newReading ?? "").trim();
    if (!val) {
      toast.error("Please enter new reading");
      return;
    }

    const r = await dispatch(
      resetMeterThunk({
        home_id,
        new_reading: val,
      })
    );

    if (resetMeterThunk.fulfilled.match(r)) {
      toast.success("Meter reset successfully ✅");
      setNewReading("");
      dispatch(fetchLastReadingThunk(home_id)); // reload
    } else {
      const msg =
        r?.payload?.message ||
        r?.payload ||
        r?.error?.message ||
        "Failed to reset meter";
      toast.error(String(msg));
    }
  };

  const onRefresh = () => {
    if (!home_id) return;
    dispatch(fetchLastReadingThunk(home_id));
    toast.info("Refreshing...");
  };

  // ✅ stop rendering when not active (important for performance)
  if (!isActive) return null;

  return (
    <div className="hm-form-page meterreset-tab">
      <Row className="g-3">
        <Col xs={12}>
          <Card className="hm-form-card">
            <Card.Header className="hm-form-header">
              <div className="meterreset-head d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1 hm-form-title">Meter Reset</h5>
                  <div className="hm-form-subtitle">
                    Update the meter reading for this house.
                  </div>
                </div>

                <div className="d-flex gap-2 align-items-center">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={onRefresh}
                    disabled={loading}
                  >
                    {loading ? "Refreshing..." : "Refresh"}
                  </Button>

                  {home_id && (
                    <Badge bg="light" text="dark" className="hm-form-badge">
                      Home ID: <span className="hm-form-badge-val">{home_id}</span>
                    </Badge>
                  )}
                </div>
              </div>
            </Card.Header>

            <Card.Body className="hm-form-body">
              {loading ? (
                <div className="hm-form-loading">
                  <Spinner animation="border" />
                  <div className="mt-2 text-muted">
                    Loading last reading...
                  </div>
                </div>
              ) : (
                <Form onSubmit={submit}>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group className="hm-field">
                        <Form.Label className="hm-label">
                          Last Reading
                        </Form.Label>
                        <Form.Control
                          className="hm-control"
                          value={lastReading?.last_reading ?? 0}
                          readOnly
                        />
                        <div className="hm-help">
                          This is the most recent reading stored in the system.
                        </div>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="hm-field">
                        <Form.Label className="hm-label">
                          New Reading <span className="hm-req">*</span>
                        </Form.Label>
                        <Form.Control
                          className="hm-control"
                          type="number"
                          required
                          value={newReading}
                          onChange={(e) => setNewReading(e.target.value)}
                          placeholder="Enter new reading"
                        />
                        <div className="hm-help">
                          Enter the updated meter reading value.
                        </div>
                      </Form.Group>
                    </Col>

                    <Col md={9} className="d-flex align-items-end">
                      <div className="hm-help">
                        Fields marked <span className="hm-req">*</span> are required.
                      </div>
                    </Col>

                    <Col md={3} className="d-flex align-items-end justify-content-end">
                      <Button
                        type="submit"
                        className="hm-primary-btn w-100"
                        disabled={saving || !home_id}
                      >
                        {saving ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Updating…
                          </>
                        ) : (
                          "Update"
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}